import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../../services/apiService";

/**
 * PaymentSuccessHandler - Handles redirect after successful Stripe payment
 *
 * Flow:
 * 1. User completes Stripe checkout
 * 2. Stripe redirects here with session_id
 * 3. This page calls API to verify session and update plan in DB
 * 4. Redirects to original page with payment_success=true&plan=<verified_plan>
 * 5. Original page shows PaymentSuccessModal with correct plan
 */
const PaymentSuccessHandler: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<string>("Confirming your subscription...");

  useEffect(() => {
    const verifyAndRedirect = async () => {
      const sessionId = searchParams.get("session_id");
      let verifiedPlan: string | null = null;

      if (sessionId) {
        setStatus("Verifying payment...");
        try {
          // Call API to verify session and update plan in database
          const response = await apiService.post("/payments/verify-and-update", {
            sessionId,
          });

          if (response.data?.success && response.data?.plan) {
            verifiedPlan = response.data.plan;
            setStatus(`Subscription confirmed: ${verifiedPlan} plan`);
          }
        } catch (error) {
          console.error("Error verifying payment:", error);
          // Continue even if verification fails - webhook may handle it
        }
      }

      // Get the stored return URL
      const returnUrl = localStorage.getItem("payment_return_url");
      localStorage.removeItem("payment_return_url");

      // Build redirect URL with payment_success and plan params
      let redirectPath = returnUrl || "/";
      const separator = redirectPath.includes("?") ? "&" : "?";
      redirectPath = `${redirectPath}${separator}payment_success=true`;

      // Add the verified plan to URL if we got it
      if (verifiedPlan) {
        redirectPath += `&plan=${verifiedPlan}`;
      }

      // Small delay to show status
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 500);
    };

    verifyAndRedirect();
  }, [navigate, searchParams]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#161616",
        color: "#fff",
      }}
    >
      <div
        style={{
          width: "48px",
          height: "48px",
          border: "3px solid #374151",
          borderTopColor: "#A855F7",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
        }}
      />
      <p style={{ marginTop: "16px", color: "#9CA3AF" }}>{status}</p>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentSuccessHandler;
